import { collection, collectionGroup, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { CreateWorkspaceRequest, CreateWorkspaceResponse, GetWorkspaceRoomsRequest, GetWorkspaceRoomsResponse, GetWSByInvitationKeyRequest, GetWSByInvitationKeyResponse, JoinRoomViaWorkspaceRequest, JoinRoomViaWorkspaceResponse, JoinWorkspaceRequest, JoinWorkspaceResponse } from '../models/DTO';
import { Workspace, WorkspaceMember, toUserSimplified } from '../models/Workspace';
import { WorkspaceStatus } from '../models/WorkspaceStatus';
import { UnifiedStorageReport } from '../models/UnifiedStorageReport';
import { FireClient, getFunc } from './FireClient';
import { pojo } from './Utils/Util';
import { My } from './My';

export class FireClientWorkspace {
  constructor(private c: FireClient) {
  }

  async createWorkspace(data: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
    const f = getFunc('createWorkspace');
    const res = await f(pojo(data));
    return res.data as CreateWorkspaceResponse;
  }

  async joinWorkspace(data: JoinWorkspaceRequest): Promise<JoinWorkspaceResponse> {
    const f = getFunc('joinWorkspace');
    const res = await f(pojo(data));
    return res.data as JoinWorkspaceResponse;
  }

  async getWorkspaceRooms(data: GetWorkspaceRoomsRequest): Promise<GetWorkspaceRoomsResponse> {
    const f = getFunc('getWorkspaceRooms');
    const res = await f(pojo(data));
    return res.data as GetWorkspaceRoomsResponse;
  }

  async getJoinedWorkspaceRooms(data: GetWorkspaceRoomsRequest): Promise<GetWorkspaceRoomsResponse> {
    const f = getFunc('getJoinedWorkspaceRooms');
    const res = await f(pojo(data));
    return res.data as GetWorkspaceRoomsResponse;
  }


  async getWorkspaceByInvitationKey(data: GetWSByInvitationKeyRequest): Promise<GetWSByInvitationKeyResponse> {
    const f = getFunc('getWorkspaceByInvitationKey');
    const res = await f(pojo(data));
    return res.data as GetWSByInvitationKeyResponse;
  }

  async joinRoomViaWorkspace(data: JoinRoomViaWorkspaceRequest): Promise<JoinRoomViaWorkspaceResponse> {
    const f = getFunc('joinRoomViaWorkspace');
    const res = await f(pojo(data));
    return res.data as JoinRoomViaWorkspaceResponse;
  }

  async getAllowedWorkspaces(domain: string) {
    const q = query(collection(this.c.store, Workspace.COL), where('emailDomains', 'array-contains', domain));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as Workspace);
  }

  /**
   * Sync owner info if current user is owner and info has changed
   */
  private async syncOwnerInfoIfNeeded(workspace: Workspace) {
    if (!My.user || workspace.owner.id !== My.user.id) return;

    const currentOwner = workspace.owner;
    const myInfo = toUserSimplified(My.user);

    // Check if name or photo has changed
    if (currentOwner.name !== myInfo.name || currentOwner.photoUrl !== myInfo.photoUrl) {
      console.log(`[Workspace] Syncing owner info for ${workspace.id}`);
      try {
        await updateDoc(doc(this.c.store, Workspace.COL, workspace.id), {
          owner: pojo(myInfo)
        });
      } catch (e) {
        console.error('[Workspace] Failed to sync owner info:', e);
      }
    }
  }

  subscribeToWorkspaces(userId: string, handler: (list: Workspace[]) => void) {
    const q = query(collectionGroup(this.c.store, WorkspaceMember.COL), where('id', '==', userId));
    return onSnapshot(q, async (snap) => {
      const workspaces: Workspace[] = [];
      for (const docSnap of snap.docs) {
        const member = docSnap.data() as WorkspaceMember;
        // If workspace is embedded (legacy), use it directly
        if (member.workspace) {
          workspaces.push(member.workspace);
        } else {
          // Extract workspaceId from document path: workspaces/{wsId}/members/{userId}
          const pathParts = docSnap.ref.path.split('/');
          const workspaceId = pathParts[1];
          const wsDoc = await getDoc(doc(this.c.store, Workspace.COL, workspaceId));
          if (wsDoc.exists()) {
            const ws = { id: wsDoc.id, ...wsDoc.data() } as Workspace;
            workspaces.push(ws);
            // Auto-sync owner info if needed
            this.syncOwnerInfoIfNeeded(ws);
          }
        }
      }
      handler(workspaces);
    });
  }

  async getWorkspace(workspaceId: string): Promise<Workspace | undefined> {
    const d = doc(this.c.store, Workspace.COL, workspaceId)
    return (await getDoc(d)).data() as Workspace | undefined;
  }

  leaveWorkspace(workspaceId: string, userId: string) {
    return deleteDoc(doc(this.c.store, Workspace.COL, workspaceId, WorkspaceMember.COL, userId));
  }

  async updateWorkspace(workspace: Workspace) {
    console.log('[FireClientWorkspace] updateWorkspace called', {
      id: workspace.id,
      name: workspace.name,
      hasOwner: !!workspace.owner
    });

    try {
      const pojoData = pojo(workspace);
      console.log('[FireClientWorkspace] pojo conversion successful, keys:', Object.keys(pojoData));

      const docRef = doc(this.c.store, Workspace.COL, workspace.id);
      console.log('[FireClientWorkspace] docRef created, calling updateDoc...');

      const result = await updateDoc(docRef, pojoData);
      console.log('[FireClientWorkspace] updateDoc completed successfully');
      return result;
    } catch (e) {
      console.error('[FireClientWorkspace] updateDoc failed:', e);
      throw e;
    }
  }


  deleteWorkspace(workspaceId: string) {
    return deleteDoc(doc(this.c.store, Workspace.COL, workspaceId));
  }

  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    const q = query(collection(this.c.store, Workspace.COL, workspaceId, WorkspaceMember.COL));
    const snap = await getDocs(q);
    const members = snap.docs.map(d => d.data() as WorkspaceMember);

    // 각 멤버의 User 문서에서 lastActiveAt 가져오기
    const userIds = members.map(m => m.id);
    const userDocs = await Promise.all(
      userIds.map(uid => getDoc(doc(this.c.store, 'users', uid)))
    );

    // lastActiveAt 병합 (Firestore Timestamp → Date 변환)
    members.forEach((member, i) => {
      const userData = userDocs[i].data();
      if (userData?.lastActiveAt) {
        // Firestore Timestamp인 경우 toDate() 호출
        const ts = userData.lastActiveAt;
        (member as any).lastActiveAt = ts?.toDate ? ts.toDate() : new Date(ts);
      }
    });

    return members;
  }

  async getWorkspaceStatus(workspaceId: string): Promise<WorkspaceStatus> {
    const d = doc(this.c.store, Workspace.COL, workspaceId, WorkspaceStatus.COL, workspaceId);
    const status = (await getDoc(d)).data() as WorkspaceStatus | undefined;
    return status ?? new WorkspaceStatus(workspaceId);
  }

  subscribeToUnifiedReport(workspaceId: string, handler: (report: UnifiedStorageReport | undefined) => void) {
    const d = doc(this.c.store, Workspace.COL, workspaceId, 'reports', 'storage_daily');
    return onSnapshot(d, (snap) => {
      handler(snap.exists() ? snap.data() as UnifiedStorageReport : undefined);
    });
  }

  async getUnifiedReport(workspaceId: string): Promise<UnifiedStorageReport | undefined> {
    const d = doc(this.c.store, Workspace.COL, workspaceId, 'reports', 'storage_daily');
    const snap = await getDoc(d);
    return snap.exists() ? snap.data() as UnifiedStorageReport : undefined;
  }

  /**
   * Super Admin용: 모든 워크스페이스 조회
   */
  async getAllWorkspaces(): Promise<Workspace[]> {
    const q = query(collection(this.c.store, Workspace.COL));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }) as Workspace);
  }
}

// async getListedWorkspaceRooms(workspaceId: string) {
//   console.log('getAllowedWorkspaces', workspaceId);
//   const q = query(collection(this.c.store, Room.COL),
//   where('workspaceId', 'array-contains', workspaceId),
//   where('visibility', '==', 'listed')
//   );
//   const snap = await getDocs(q);
//   return snap.docs.map(d => d.data() as Workspace);
// }